import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share } from "lucide-react";

// Detects Safari on iOS/iPadOS (not Chrome/Firefox on iOS, which can't install PWAs)
function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
  return isIOS && isSafari;
}

function isInStandaloneMode() {
  return window.navigator.standalone === true
    || window.matchMedia("(display-mode: standalone)").matches;
}

export default function IOSInstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Only show on iOS Safari when not already installed as PWA
    if (!isIOSSafari() || isInStandaloneMode()) return;
    // Don't show if dismissed within the last 7 days
    const dismissed = localStorage.getItem("ios_install_dismissed");
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;
    // Small delay so it doesn't flash on load
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem("ios_install_dismissed", String(Date.now()));
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 380 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-6 pt-0"
          style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 flex items-start gap-3 max-w-sm mx-auto">
            <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0 text-white font-bold font-sora text-sm shadow-sm">
              EP
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sora font-bold text-[14px] text-slate-800 leading-snug">
                Install EduPulseAI
              </p>
              <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">
                Tap <Share size={12} className="inline-block mx-0.5 text-blue-500" /> then <strong>"Add to Home Screen"</strong> to get push notifications and offline access.
              </p>
            </div>
            <button
              onClick={dismiss}
              className="shrink-0 w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer border-0"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
